import React, { useState } from 'react';

import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Pagination from '@material-ui/lab/Pagination';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import { t } from '../../providers';

const useStyles = makeStyles((theme) => ({
    topBarInner: {
        padding: '10px 0px 60px',
        color: '#6f6c6c',
    },
    topBarLeft: {
        fontSize: '0.875rem',
        fontWeight: '100',
        color: '#4D4D4D',
        paddingTop: '3px',
    },
    topBarRight: {
        fontSize: '0.875rem',
        fontWeight: '100',
        float: 'right',
        color: '#4D4D4D',
        '@media (max-width:1100px)': {
            marginRight: '0px',
        },
    },
    smallText: {
        fontSize: '0.875rem',
        display: 'inline-block',
        padding: ' 0px 6px 0px 0px',
        color: theme.palette.common.darkGrey,
        '@media (max-width:1100px)': {
            marginBottom: '10px',
            textAlign: 'center',
        },
    },
    nav: {
        color: theme.palette.common.blue,
        '&:hover': {
            cursor: 'pointer',
        },
    },
    navInactive: {
        color: 'lightgray',
        pointerEvents: 'none',
    },
    pagination: {
        color: '#000000',
        '& .MuiButtonBase-root': {
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
        },
        '& .MuiPaginationItem-page.Mui-selected': {
            background: '#E2EFD5 !important',
            color: '#223211',
            borderRadius: '0px',
        },
        '& .MuiPaginationItem-root': {
            color: theme.palette.text.mainGreen,
        },
        '& .MuiPaginationItem-page:hover': {
            background: '#E2EFD5',
            borderRadius: '0px',
            '@media (max-width:600px)': {
                background: 'none',
            },
        },
    },
    pageSizeSelector: {
        border: 'none',
        width: '8.5rem',
        '& .MuiSelect-select': {
            marginLeft: '11px',
            padding: '7px 11px',
            color: theme.palette.text.mainGreen,
            fontSize: '0.875rem',
            borderRadius: '4px',
            background: 'transparent',
            fontWeight: '600',
            lineHeight: '20px',
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
        },
    },
    topBarInnerSmall: {
        padding: '10px 15px 60px',
        color: '#6f6c6c',
        '@media (max-width:960px)': {
            flexDirection: 'column-reverse',
            padding: '5px 15px 60px',
        },
        '@media (max-width:1100px)': {
            textAlign: 'center',
        },
    },
    topBarLeftSmall: {
        fontSize: '0.875rem',
        fontWeight: '100',
        color: '#4D4D4D',
        paddingTop: '3px',
        '@media (max-width:960px)': {
            marginBottom: '20px',
        },
    },
    topBarRightSmall: {
        fontSize: '0.875rem',
        fontWeight: '100',
        float: 'right',
        color: '#4D4D4D',
        marginRight: '0px',
        '@media (max-width:960px)': {
            float: 'unset',
            paddingBottom: '5px',
        },
    },
    pageSizeOption: {
        background: '#fff',
        '&:checked': {
            background: '#BDE298',
        },
        '&:hover': {
            background: '#BDE298',
        },
    },
    pageSizeSelectorSmall: {
        border: 'none',
        width: '8.5rem',
        '& .MuiSelect-select': {
            marginLeft: '11px',
            padding: '7px 11px',
            color: theme.palette.text.mainGreen,
            fontSize: '0.875rem',
            borderRadius: '4px',
            background: 'transparent',
            fontWeight: '600',
            lineHeight: '20px',
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
        },
    },
}));

const CustomPagination = ({
    content,
    totalElements,
    component,
    count,
    size,
    page,
    onChangePage,
    onChangeRowsPerPage,
    selectProps,
    paginationId,
    isTablet,
    lastUpdateDate,
}) => {
    /**
     * pages options generated based on totalElements and default page size
     * Example:
     * Default value is 10.
     * Possibile options:  increment of 10 based on total number of items
     */
    const [selectIsOpen, setSelectIsOpen] = useState(false);
    const classes = useStyles();
    let disableLeft = false;
    let disableRight = false;
    let rowsPerPageOptions = [];
    const totalPages = totalElements % size > 0 ? Math.floor(totalElements / size) + 1 : Math.floor(totalElements / size);
    const totalPages2 = totalElements % 10 > 0 ? Math.floor(totalElements / 10) + 1 : Math.floor(totalElements / 10);

    for (let i = 1; i <= totalPages2; i++) {
        let pageSizeOption = i * 10;
        rowsPerPageOptions.push(pageSizeOption);
    }

    let navClassRight = classes.nav;
    if (totalElements <= page * size) {
        navClassRight = classes.navInactive;
        disableRight = true;
    }
    let navClassLeft = classes.nav;
    if (page === 1) {
        navClassLeft = classes.navInactive;
        disableLeft = true;
    }

    let currentPage = page + 1;

    const changePage = (event, newPage) => {
        onChangePage(event, newPage - 1);
    };

    const changePageSize = (value) => {
        onChangeRowsPerPage(value);
    };

    const parseTimestamp = (timestamp) => {
        let date = new Date(timestamp * 1);
        let day = date.getDate();
        let month = date.getMonth() + 1;
        let year = date.getFullYear();
        // Hours part from the timestamp
        let hours = date.getHours();
        // Minutes part from the timestamp
        let minutes = '0' + date.getMinutes();
        // Seconds part from the timestamp
        let seconds = '0' + date.getSeconds();

        // Will display time in 10:30:23 format
        let formattedTime = hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2) + ' of ' + day + '/' + month + '/' + year;
        return formattedTime;
    };

    const selectClose = () => {
        setSelectIsOpen(false);
    };

    const selectOpen = () => {
        setSelectIsOpen(true);
    };

    return (
        <div>
            {!isTablet && (
                <Grid container spacing={0} className={classes.topBarInner}>
                    {paginationId !== 'myDrafts' && (
                        <Grid item sm={6} className={classes.topBarLeft}>
                            {t('lisbon.timestamp')}{' '}
                            {content[0] && content[0].lastUpdateDate ? content[0].lastUpdateDate : lastUpdateDate ? lastUpdateDate : ''}
                        </Grid>
                    )}
                    {paginationId === 'myDrafts' && (
                        <Grid item sm={6} className={classes.topBarLeft}>
                            {' '}
                        </Grid>
                    )}
                    <Grid item sm={6}>
                        <Grid display="inline" className={classes.topBarRight}>
                            <div className={classes.smallText}>
                                {t('showing')} {(page + 1) * size - size + 1} {t('pagination.to')}{' '}
                                {(page + 1) * size < totalElements ? (page + 1) * size : totalElements} {t('of')} {totalElements} {t('entries')}
                            </div>
                            <div style={{ display: 'inline-block' }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    siblingCount={1}
                                    className={classes.pagination}
                                    onChange={changePage}
                                />
                            </div>
                            <Select
                                SelectDisplayProps={{
                                    'aria-label': `${t('results.per.page')}`,
                                    role: 'combobox',
                                    'aria-controls': 'results-per-page-list',
                                    'aria-expanded': selectIsOpen,
                                    'aria-labelledby': undefined,
                                }}
                                id={'results-per-page-select'}
                                className={classes.pageSizeSelector}
                                displayEmpty
                                onOpen={selectOpen}
                                onClose={selectClose}
                                onChange={(e) => {
                                    changePageSize(e.target.value);
                                }}
                                value={size}
                                MenuProps={{
                                    'aria-haspopup': true,
                                    MenuListProps: { role: 'listbox', id: 'results-per-page-list', 'aria-labelledby': 'results-per-page-select' },
                                }}>
                                {rowsPerPageOptions.map((pageSize, idx) => (
                                    <MenuItem style={{ background: '#fff' }} className={classes.pageSizeOption} key={pageSize} value={pageSize}>
                                        {pageSize !== 9999 ? `${pageSize} ${t('items')}` : t('all')}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                    </Grid>
                </Grid>
            )}
            {isTablet && (
                <Grid container spacing={0} className={classes.topBarInnerSmall}>
                    {paginationId !== 'myDrafts' && (
                        <Grid item sm={12} className={classes.topBarLeftSmall}>
                            {t('lisbon.timestamp')}{' '}
                            {content[0] && content[0].lastUpdateDate ? content[0].lastUpdateDate : lastUpdateDate ? lastUpdateDate : ''}
                        </Grid>
                    )}
                    {paginationId === 'myDrafts' && (
                        <Grid item sm={12} className={classes.topBarLeftSmall}>
                            {' '}
                        </Grid>
                    )}
                    <Grid item sm={12}>
                        <Grid display="inline" className={classes.topBarRightSmall}>
                            <div className={classes.smallText}>
                                {t('showing')} {(page + 1) * size - size + 1} {t('pagination.to')}{' '}
                                {(page + 1) * size < totalElements ? (page + 1) * size : totalElements} {t('of')} {totalElements} {t('entries')}
                            </div>
                            <div style={{ display: 'inline-block' }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    siblingCount={1}
                                    className={classes.pagination}
                                    onChange={changePage}
                                />
                            </div>
                            <Select
                                SelectDisplayProps={{
                                    'aria-label': `${t('results.per.page')}`,
                                    role: 'combobox',
                                    'aria-controls': 'results-per-page-list',
                                    'aria-expanded': selectIsOpen,
                                    'aria-labelledby': undefined,
                                }}
                                id={'results-per-page-select'}
                                className={classes.pageSizeSelectorSmall}
                                displayEmpty
                                onOpen={selectOpen}
                                onClose={selectClose}
                                onChange={(e) => {
                                    changePageSize(e.target.value);
                                }}
                                value={size}
                                MenuProps={{
                                    'aria-haspopup': true,
                                    MenuListProps: { role: 'listbox', id: 'results-per-page-list', 'aria-labelledby': 'results-per-page-select' },
                                }}>
                                {rowsPerPageOptions.map((pageSize, idx) => (
                                    <MenuItem style={{ background: '#fff' }} className={classes.pageSizeOption} key={pageSize} value={pageSize}>
                                        {pageSize !== 9999 ? `${pageSize} ${t('items')}` : t('all')}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </div>
    );
};

export default CustomPagination;
